import { useEffect, useState } from 'react';
import { ApiError } from '../../api/client';
import { listReportKinds, reportStory, type ReportKind } from '../../api/reports';
import { Alert, Button, Card } from '../ui';
import './ReportControl.css';

/**
 * Tell the instance that something is wrong with a story.
 *
 * Closed behind a single quiet link rather than sitting open on the page:
 * reporting is a serious thing to do about somebody's work, and a form nobody
 * had to open is a form people fill in by accident.
 *
 * What it promises is exactly what happens. Filing a report changes nothing
 * about the story — a moderator reads it and decides — and the wording says so,
 * because a reader who thinks reporting removes things will report to remove
 * things.
 */
export function ReportControl({ postId }: { postId: string }) {
  const [open, setOpen] = useState(false);
  const [kinds, setKinds] = useState<ReportKind[]>([]);
  const [kind, setKind] = useState('');
  const [details, setDetails] = useState('');
  const [sent, setSent] = useState(false);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    if (!open || kinds.length) {
      return;
    }
    listReportKinds()
      .then((found) => {
        setKinds(found);
        setKind(found[0]?.value ?? '');
      })
      .catch(() => setKinds([]));
  }, [open, kinds.length]);

  const send = async () => {
    setWorking(true);
    setError(null);
    try {
      await reportStory(postId, kind, details);
      setSent(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setWorking(false);
    }
  };

  if (sent) {
    return (
      <Alert tone="info" title="Thank you">
        A moderator will read this. You can see where it got to under your account.
      </Alert>
    );
  }

  if (!open) {
    return (
      <p className="sc-report__open">
        <button type="button" className="sc-report__link" onClick={() => setOpen(true)}>
          Report this story
        </button>
      </p>
    );
  }

  return (
    <Card>
      <h2 className="sc-report__title">Report this story</h2>
      <p className="sc-report__muted">
        This does not take anything down. A moderator reads what you write and decides.
      </p>

      {error && (
        <Alert tone="warning" title="That was not sent">
          {error.message}
        </Alert>
      )}

      <label className="sc-report__field">
        <span>What is wrong with it</span>
        <select value={kind} onChange={(event) => setKind(event.target.value)}>
          {kinds.map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choice.label}
            </option>
          ))}
        </select>
      </label>

      <label className="sc-report__field">
        <span>What a moderator should check</span>
        <textarea
          rows={4}
          value={details}
          onChange={(event) => setDetails(event.target.value)}
          placeholder="The chart shows 12% and the sentence says it doubled."
        />
      </label>

      <div className="sc-report__actions">
        <Button size="sm" isLoading={working} onClick={() => void send()}>
          Send it
        </Button>
        <Button size="sm" variant="secondary" onClick={() => setOpen(false)}>
          Never mind
        </Button>
      </div>
    </Card>
  );
}
